const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const SwitchUserError = require('@/lib/UserSessions/SwitchUserError');
const TradingSessionEvent = require('@/lib/TradingSessions/TradingSessionEvent');
const TradingSessionEventType = require('@/lib/TradingSessions/TradingSessionEventType');
const User = require('@/lib/Users/User');
const { apiResponseToError } = require('@/lib/Api');

/**
 * @constructor
 *
 * @param {CurrentDevice} currentDevice
 * @param {DeviceManager} deviceManager
 * @param {Hydrator} hydrator
 * @param {NavigationService} navigationService
 * @param {PrivilegesManager} privilegesManager
 * @param {TradingSessionManager} tradingSessionManager
 * @param userResource
 * @param {UserService} userService
 * @param {UserSessionManager} userSessionManager
 */
const UserSwitcher = function (
    currentDevice,
    deviceManager,
    hydrator,
    navigationService,
    privilegesManager,
    tradingSessionManager,
    userResource,
    userService,
    userSessionManager
) {
    /**
     * @private
     * @type {CurrentDevice}
     */
    this.currentDevice = currentDevice;

    /**
     * @private
     * @type {DeviceManager}
     */
    this.deviceManager = deviceManager;

    /**
     * @private
     * @type {Hydrator}
     */
    this.hydrator = hydrator;

    /**
     * @private
     * @type {ConsoleLogger}
     */
    this.logger = logFactory('UserSwitcher');

    /**
     * @private
     * @type {NavigationService}
     */
    this.navigationService = navigationService;

    /**
     * @private
     * @type {PrivilegesManager}
     */
    this.privilegesManager = privilegesManager;

    /**
     * @private
     * @type {TradingSessionManager}
     */
    this.tradingSessionManager = tradingSessionManager;

    /**
     * @private
     */
    this.userResource = userResource;

    /**
     * @private
     * @type {UserService}
     */
    this.userService = userService;

    /**
     * @private
     * @type {UserSessionManager}
     */
    this.userSessionManager = userSessionManager;
};

UserSwitcher.prototype = {
    /**
     * Switch to a new user.
     *
     * @param {('pin'|'rfid-tag')} method
     * @param {string|number} enteredValue The PIN or RFID tag value.
     *
     * @return {Promise<User>}
     */
    async switchUser(method, enteredValue) {
        if (method !== 'pin' && method !== 'rfid-tag') {
            throw new Error("Method must be one of 'pin' or 'rfid-tag'.");
        }

        // Work out who the new user is. Ask the privileges model to find the user with that PIN or Tag Id.
        let newUserPrivileges;
        try {
            newUserPrivileges = await this.privilegesManager.findPrivileges(method, enteredValue);
        } catch (e) {
            // Check if it matched a user and that that user is not the one currently logged in.
            /* istanbul ignore else */
            if (method === 'pin') {
                throw new SwitchUserError('No user with that PIN was found.', false);
            } else if (method === 'rfid-tag') {
                throw new SwitchUserError('No user with that RFID tag was found.', false);
            }
        }

        // Ensure the user's session is unlocked.
        await this.userSessionManager.unlockAll();

        // Switching to same user. Nothing to do.
        if (this.userService.currentUser.ID === newUserPrivileges.userID) {
            return this.userService.currentUser;
        }

        // Get the username of the user we're going to switch to.
        const newUsername = newUserPrivileges.username;

        this.logger.log(`User "${this.userService.username}" requested to switch to user "${newUsername}"`);

        // Stash the state of the current user.
        if (this.userService.currentUserSession) {
            this.userService.currentUserSession.lastScreen = this.navigationService.getCurrentPath();
            await this.userService.persistCurrentUserSession();
        }

        // Clear stored API keys for users that logged in over 24 hours ago.
        await this.userSessionManager.pruneOldAuthData();

        // Check if we already have a session and apiKey for the other user. If we do, skip the login screen
        // and go straight to that user.
        let newUsersSession = await this.userSessionManager.getSession(newUserPrivileges.userID);

        if (!newUsersSession) {
            this.redirectToLogin(newUsername);
            throw new SwitchUserError('Did not find existing session for new user.', true);
        }

        if (!newUsersSession.apiKey) {
            this.redirectToLogin(newUsername);
            throw new SwitchUserError('New user session did not contain an API key.', true);
        }

        return new Promise((resolve, reject) => {
            // Check the new user's API key is still valid by making a request with it.
            this.userResource.check(
                {
                    apiKey: newUsersSession.apiKey,
                },
                async (response) => {
                    if (response && response.user) {
                        // Successful login.

                        let user = this.hydrator.hydrate(response.user, new User());
                        // The user returned by the API does not contain an apiKey!
                        user.apiKey = newUsersSession.apiKey;
                        console.log('New user', user);

                        // Check the new user has access to the current branch.
                        // If they don't we will force a logout.
                        const currentBranchForNewUser = user.getBranchByID(this.userService.currentBranch.ID);
                        if (!currentBranchForNewUser) {
                            reject(new SwitchUserError('New user does not have access to current branch.', true));
                            this.redirectToLogin(newUsername);
                            return;
                        }

                        this.tradingSessionManager.saveEvent(
                            (new TradingSessionEvent(TradingSessionEventType.USER_SWITCH))
                                .setOperatorID(response.user.ID)
                        );

                        // Update the current user to be the new one.
                        await this.userService.changeUser(user);

                        this.navigationService.viewEposIndex();
                        resolve(user);
                    } else {
                        // Bad login. Redirect to the login screen, with the "Switching User" message displayed.
                        console.warn('Bad API response for new user API key.', response);
                        reject(new SwitchUserError('Bad API response for new user API key.', true));
                        this.redirectToLogin(newUsername);
                    }
                },
                (result) => {
                    // Bad login. Redirect to the login screen, with the "Switching User" message displayed.
                    const error = apiResponseToError(result);
                    reject(new SwitchUserError('Bad API response for new user API key: ' + error.message));
                    this.redirectToLogin(newUsername);
                }
            );
        });
    },

    /**
     * Send user to the login screen, prefill the username, and prompt for password.
     *
     * @private
     * @param {string} autofillUsername
     */
    redirectToLogin(autofillUsername) {
        // Autofill that username on the login page.
        this.userService.setLoginErrorMessageKey('LOGIN.ERROR__RE_LOGIN_FOR_SWITCH');
        this.userService.setAutofillLoginUsername(autofillUsername);

        // Redirect to the login screen, with the "Switching User" message displayed.
        this.userService.logout('SWITCH_USER.PROCESSING_SWITCH');
    },

};

module.exports = UserSwitcher;
